import Modal, { Props as ModalProps } from "@common/components/Modal";
import { useRouter } from "next/router";
import useExtendedWarrantyDefaultRates from "@modules/purchase/hooks/useExtendedWarrantyDefaultRates";
import { Purchase } from "@modules/purchase/types/Purchase";
import useCustomerInfoFromQuery from "@modules/query-string/hooks/useCustomerInfoFromQuery";
import useIsBuyerProvinceActiveForAftermarket from "@modules/purchase/hooks/useIsBuyerProvinceActiveForAftermarket";
import { getProvinceByAbbr } from "@common/types/Province";
import LoadingSpinner from "@common/components/LoadingSpinner";
import { useTranslation } from "next-i18next";
import { GetMainApplicant } from "@modules/purchase/helpers/purchaseCustomerHelper";
import usePageInfoFromQuery from "@modules/query-string/hooks/usePageInfoFromQuery";
import WarrantyGraphChart from "./WarrantyGraphChart";
import WarrantyComponent from "./WarrantyComponent";
import RemainingWarranty from "./RemainingWarranty";
import { RANGE_QUOTE_SEGMENT } from "./types/DealSegmentConstants";
import WarrantyBrochure from "./WarrantyBrochure";

type Props = {
  purchase: Purchase;
  showChart?: boolean;
} & Pick<ModalProps, "isOpen" | "onClose">;

const CoverageDetailsModal = ({
  purchase,
  isOpen,
  onClose,
  showChart,
}: Props) => {
  const router = useRouter();
  const { t } = useTranslation(["purchase"]);
  const locale = router?.locale || "en";
  const { province: provinceQuery } = useCustomerInfoFromQuery();
  const source = purchase.dealSource;
  const { vehicle } = purchase;
  const mainApplicant = GetMainApplicant(purchase);
  const { isProvinceActive, isProvincesLoading } =
    useIsBuyerProvinceActiveForAftermarket(
      source,
      provinceQuery ||
        getProvinceByAbbr(mainApplicant?.currentAddress?.provinceAbbr ?? "")
    );

  const { dealSegment } = usePageInfoFromQuery();

  const extDefaultRatesEnabled = dealSegment !== RANGE_QUOTE_SEGMENT;

  const {
    data: extDefaultRates,
    isSuccess,
    isLoading,
  } = useExtendedWarrantyDefaultRates(
    {
      vin: vehicle?.vin ?? "",
      odometer: vehicle?.odometerCount ?? 0,
      province:
        provinceQuery?.abbr ||
        mainApplicant?.currentAddress?.provinceAbbr ||
        undefined,
      source,
      year: vehicle?.year?.toString() ?? "",
      make: vehicle?.make ?? "",
      model: vehicle?.model ?? "",
    },
    isProvincesLoading ? null : isProvinceActive,
    locale,
    extDefaultRatesEnabled
  );

  const vehicleWarranty = extDefaultRates?.vehicleWarranty;
  const isVehicleWarrantyFetched = !!vehicleWarranty;

  return (
    <Modal
      isOpen={isOpen}
      alignment="center"
      onClose={onClose}
      title={t("purchase:whats_covered")}
    >
      <LoadingSpinner isLoading={isLoading} isOverlay />
      {isSuccess && (
        <>
          {showChart && vehicleWarranty && (
            <>
              <div className="mx-auto text-center">
                <RemainingWarranty vehicleWarranty={vehicleWarranty} isModal />
              </div>
              <div className="w-full pb-8">
                <WarrantyGraphChart
                  vehicleWarranty={vehicleWarranty}
                  redraw={isVehicleWarrantyFetched}
                />
              </div>
            </>
          )}

          <div className="text-left rounded-lg px-4">
            <WarrantyComponent extendedWarrantyDefaultRates={extDefaultRates} />

            <WarrantyBrochure extWarrantyRates={extDefaultRates} />
          </div>
        </>
      )}
    </Modal>
  );
};

export default CoverageDetailsModal;
